import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Grid2 as Grid,
  CardMedia,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Typography,
  Box,
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowBackIosNew as ArrowBackIosNewIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { useObservation } from 'app/contexts/ObservationContext';
import { formatInTimeZone } from 'date-fns-tz';

// === Helpers (pure, declared once) ===
const formatDate = (ts) =>
  formatInTimeZone(
    new Date(`${ts}Z`),
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    'MM/dd/yyyy HH:mm:ss',
  );

const defaultEmail = (email) => email ?? 'Not a project member.';

// === Custom Hook for ArrowKey Navigation ===
function useKeyNavigation(open, canNavigate, onPrev, onNext) {
  useEffect(() => {
    if (!open || !canNavigate) return;
    const handler = (e) => {
      if (e.key === 'ArrowLeft') onPrev();
      else if (e.key === 'ArrowRight') onNext();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, canNavigate, onPrev, onNext]);
}

// === Image Display Section ===
const ImageSection = memo(function ({
  imageUrl,
  isLoaded,
  handlePrev,
  handleNext,
  canNavigate,
  onLoad,
}) {
  return (
    <Box
      position="relative"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <IconButton
        onClick={handlePrev}
        disabled={!canNavigate}
        sx={{ position: 'absolute', left: 0 }}
      >
        <ArrowBackIosNewIcon />
      </IconButton>

      {!isLoaded && (
        <CircularProgress sx={{ position: 'absolute', zIndex: 1 }} />
      )}
      <CardMedia
        component="img"
        image={imageUrl}
        alt="Carousel"
        onLoad={onLoad}
        sx={{
          width: '100%',
          maxHeight: 500,
          objectFit: 'contain',
          opacity: isLoaded ? 1 : 0,
          transition: 'opacity 0.5s ease-in-out',
        }}
      />

      <IconButton
        onClick={handleNext}
        disabled={!canNavigate}
        sx={{ position: 'absolute', right: 0 }}
      >
        <ArrowForwardIosIcon />
      </IconButton>
    </Box>
  );
});

// === Metadata Display Section ===
const MetadataSection = memo(function ({ metadata, locationMap }) {
  if (!metadata) return null;
  const {
    timeStamp,
    latitude,
    longitude,
    locationId,
    deviceType,
    createdByEmail,
    sitePositionX,
    sitePositionY,
    sitePositionZ,
    procoreImageId,
  } = metadata;

  const coords =
    Number.isFinite(latitude) && Number.isFinite(longitude)
      ? `Latitude: ${latitude}, Longitude: ${longitude}`
      : 'N/A';

  const position = [sitePositionX, sitePositionY, sitePositionZ].every(
    Number.isFinite,
  )
    ? `X: ${sitePositionX}, Y: ${sitePositionY}, Z: ${sitePositionZ}`
    : 'N/A';

  return (
    <Box mt={2}>
      <Typography variant="h6">Photo Details</Typography>
      <Typography variant="body2">
        <strong>Timestamp:</strong> {timeStamp ? formatDate(timeStamp) : 'N/A'}
      </Typography>
      <Typography variant="body2">
        <strong>Coordinates:</strong> {coords}
      </Typography>
      <Typography variant="body2">
        <strong>Location:</strong>{' '}
        {locationMap.get(locationId) ?? 'Unknown Location'}
      </Typography>
      <Typography variant="body2">
        <strong>Device:</strong> {deviceType ?? 'N/A'}
      </Typography>
      <Typography variant="body2">
        <strong>By:</strong> {defaultEmail(createdByEmail)}
      </Typography>
      <Typography variant="body2">
        <strong>Position:</strong> {position}
      </Typography>
      {procoreImageId && (
        <Typography variant="body2">
          <strong>Procore ID:</strong> {procoreImageId}
        </Typography>
      )}
    </Box>
  );
});

export default function ImageCarouselDialog({
  open,
  onClose,
  images,
  initialIndex,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { locations } = useObservation();

  const locationMap = useMemo(() => {
    const m = new Map();
    Object.values(locations.locations || {}).forEach(({ id, name }) => {
      m.set(id, name);
    });
    return m;
  }, [locations]);

  const [idx, setIdx] = useState(initialIndex);
  const [loaded, setLoaded] = useState(false);
  const [showAR, setShowAR] = useState(true);

  useEffect(() => {
    if (open) {
      setIdx(initialIndex);
      setLoaded(false);
      setShowAR(true);
    }
  }, [open, initialIndex]);

  const current = useMemo(() => images[idx] || {}, [images, idx]);
  const canNavigate = images.length > 1;

  const imageUrl = useMemo(
    () => (showAR ? current.image?.augmentedUrl : current.image?.worldUrl),
    [showAR, current],
  );

  // Reset loading state whenever URL changes
  useEffect(() => setLoaded(false), [imageUrl]);

  const prev = useCallback(() => {
    if (!canNavigate) return;
    setIdx((i) => (i === 0 ? images.length - 1 : i - 1));
    setShowAR(true);
  }, [canNavigate, images.length]);

  const next = useCallback(() => {
    if (!canNavigate) return;
    setIdx((i) => (i === images.length - 1 ? 0 : i + 1));
    setShowAR(true);
  }, [canNavigate, images.length]);

  const download = useCallback(() => {
    if (!imageUrl) return;
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = imageUrl.split('/').pop();
    document.body.appendChild(link);
    link.click();
    link.remove();
  }, [imageUrl]);

  const toggleView = useCallback(() => setShowAR((v) => !v), []);

  useKeyNavigation(open, canNavigate, prev, next);

  if (!open || !images.length) return null;

  return (
    <Dialog open fullWidth fullScreen={isMobile} onClose={onClose}>
      <DialogTitle sx={{ position: 'relative', p: 2 }}>
        {current.metadata?.timeStamp
          ? formatDate(current.metadata.timeStamp)
          : 'Image'}

        <IconButton
          onClick={download}
          sx={{ position: 'absolute', top: 8, right: 96 }}
        >
          <DownloadIcon />
        </IconButton>
        <IconButton
          onClick={toggleView}
          sx={{ position: 'absolute', top: 8, right: 48 }}
        >
          <Typography variant="button">{showAR ? 'AR' : 'World'}</Typography>
        </IconButton>
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', top: 8, right: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Grid xs={12} md={6} sx={{ maxHeight: '50vh' }}>
          <ImageSection
            imageUrl={imageUrl}
            isLoaded={loaded}
            onLoad={() => setLoaded(true)}
            handlePrev={prev}
            handleNext={next}
            canNavigate={canNavigate}
          />
        </Grid>
        <Grid xs sx={{ overflowY: 'auto' }}>
          <MetadataSection
            metadata={current.image?.metadata}
            locationMap={locationMap}
          />
        </Grid>
      </DialogContent>
    </Dialog>
  );
}

ImageCarouselDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  images: PropTypes.arrayOf(
    PropTypes.shape({
      augmentedUrl: PropTypes.string.isRequired,
      worldUrl: PropTypes.string,
      metadata: PropTypes.object,
    }),
  ).isRequired,
  initialIndex: PropTypes.number,
};
